// pages

import { PerformanceRouteNameEnum } from '~/types/_piracy/common';

const RiotAuthOkta = lazy(() => import('../components/User/RiotAuthOkta'));
const RiotAuthOktaCallback = lazy(() => import('../components/User/RiotAuthOktaCallback'));
const OktaAuth = lazy(() => import('../components/User/OktaAuth'));
const OktaAuthCallback = lazy(() => import('../components/User/OktaAuthCallback'));
const LoginPage = lazy(() => import('../components/User/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../components/User/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../components/User/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('../components/User/VerifyEmailPage'));
const SignUpPage = lazy(() => import('../components/User/SignUpPage'));
const LoginWithToken = lazy(() => import('../components/User/LoginWithToken'));

const PDFViewer = lazy(() => import('~/components/PDFViewer'));
const HomePage = lazy(() => import('~/pages/Home'));
const SettingsPage = lazy(() => import('~/pages/Settings'));
const InfringingImagesPage = lazy(() => import('~/pages/Portfolio/InfringingImages'));

const MarketplacePage = lazy(() => import('~/pages/Marketplace'));
const SocialMediaPage = lazy(() => import('~/pages/SocialMedia'));
const WebsiteContentPage = lazy(() => import('~/pages/WebsiteContent'));
const DomainNamesPage = lazy(() => import('~/pages/DomainNames'));

const CounterfeitAssetsPage = lazy(() => import('~/pages/Portfolio/Assets/_counterfeit'));
const PiracyAssetsPage = lazy(() => import('~/pages/Portfolio/Assets/_piracy'));

const IpPortfolioPage = lazy(() => import('~/pages/Portfolio/IPDocuments'));
const OfficialImagesPages = lazy(() => import('~/pages/Portfolio/OfficialImages'));
const AuthorizedSeller = lazy(() => import('~/pages/Portfolio/AuthorizedSeller'));
const PerformanceOverviewPage = lazy(() => import('~/pages/Performance/Overview'));
const PerformanceEnforcementAnalysisPage = lazy(() => import('~/pages/Performance/_counterfeit/EnforcementAnalysis'));
const PerformanceSellerAnalysisPage = lazy(() => import('~/pages/Performance/_counterfeit/SellerAnalysis'));
const PerformanceMarketplaceComplianceRatePage = lazy(
  () => import('~/pages/Performance/_counterfeit/MarketplaceComplianceRate')
);
const PerformanceReportsPage = lazy(() => import('~/pages/Performance/Reports'));
const PiracyPerformanceEnforcementAnalysisPage = lazy(() => import('~/pages/Performance/_piracy/EnforcementAnalysis'));
const PiracyPerformanceComplianceRatePage = lazy(() => import('~/pages/Performance/_piracy/ComplianceRate'));
const PiracyPerformanceDomainProfileAnalysisPage = lazy(
  () => import('~/pages/Performance/_piracy/DomainProfileAnalysis')
);
const SellerIntelligenceLabPage = lazy(() => import('~/pages/SellerIntelligence/Lab'));
const SellerPage = lazy(() => import('~/pages/SellerAnalytics'));

const MAPMonitoring = lazy(() => import('~/pages/MAPMonitoring'));
const ConversationalAssistantPage = lazy(() => import('~/pages/ConversationalAssistant'));

const NotFoundPage = lazy(() => import('~/pages/ErrorPage/NotFound'));

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, redirect, RouteObject } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import LoadingCircularProgress from '~/components/LoadingCircularProgress';
import App from '~/App';
import LayoutWrapper from '~/components/Layouts/LayoutWrapper';
import AxiosInterceptorWrapper from '~/components/Dashboard/AxiosInterceptorWrapper';
import { ErrorBoundaryView } from '~/pages/ErrorPage/ErrorBoundary';
import { fetchAuth, getUserInfoQuery } from '~/apis/auth';
import { MarqBrandEnumsCvassistantCvAssistantTabEnum } from '~/swagger/data-contracts';
import { PermissionMap } from './permissionMap';
import { MarketplaceRouteEnum, SettingsRouteEnum } from './@types';

const MarqCommerceRoutes: RouteObject[] = [
  {
    path: '',
    loader: async () => {
      const authData = await fetchAuth();
      if (authData) {
        return redirect('/home');
      } else {
        return redirect('/login');
      }
    },
    errorElement: <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryView errorData={errorData} />} />,
  },
  {
    path: '/',
    element: <App />,
    errorElement: <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryView errorData={errorData} />} />,
    children: [
      //#region >>>>> Sign up / oAuth Login
      {
        path: 'auth',
        children: [
          {
            path: 'okta',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <RiotAuthOkta />
              </Suspense>
            ),
          },
          {
            path: 'okta-callback',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <RiotAuthOktaCallback />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'okta',
        children: [
          {
            path: 'auth',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <OktaAuth />
              </Suspense>
            ),
          },
          {
            path: 'callback',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <OktaAuthCallback />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'dashboard/:access_token/:refresh_token/:opscc_user',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <LoginWithToken />
          </Suspense>
        ),
      },
      {
        path: 'resetpw',
        children: [
          {
            path: ':token',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <ResetPasswordPage />
              </Suspense>
            ),
          },
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <ForgotPasswordPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'verifyEmail/:token',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <VerifyEmailPage />
          </Suspense>
        ),
      },
      {
        path: 'signup/:token',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <SignUpPage />
          </Suspense>
        ),
      },
      //#endregion <<<<<< Sign up / oAuth Login

      {
        path: 'login',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '',
        element: <LayoutWrapper />,
        loader: async () => {
          const authData = await fetchAuth();
          if (!authData) {
            return redirect('/login');
          }
          return null;
        },
        children: [
          {
            path: 'home',
            index: true,
            loader: async () => {
              const hasAccess = await PermissionMap.Home();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <HomePage />
              </Suspense>
            ),
          },
          {
            path: 'settings/:settingsTab?/:subTab?',
            loader: async ({ params }) => {
              if (!Object.values(SettingsRouteEnum).includes((params.settingsTab as SettingsRouteEnum) || '')) {
                return redirect('/settings/account');
              } else {
                const hasAccess = await PermissionMap.SettingTab();
                if (
                  (params.settingsTab === 'billing' && !hasAccess.billing) ||
                  (params.settingsTab === 'people' && !hasAccess.people) ||
                  (params.settingsTab === 'credits' && !hasAccess.credits)
                ) {
                  return redirect('/settings/account');
                } else {
                  return new Response(JSON.stringify(hasAccess), {
                    status: 200,
                  });
                }
              }
            },
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <SettingsPage />
              </Suspense>
            ),
          },

          {
            path: 'performance',
            loader: async () => {
              const hasAccess = await PermissionMap.Performance();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            children: [
              {
                path: '',
                index: true,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PerformanceOverviewPage />
                  </Suspense>
                ),
              },
              {
                path: 'overview',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PerformanceOverviewPage />
                  </Suspense>
                ),
              },
              {
                path: 'enforcement',
                loader: async ({ params }) => {
                  if (!params.tab) {
                    return redirect('detected');
                  } else {
                    return null;
                  }
                },
                children: [
                  {
                    path: ':tab',
                    element: (
                      <Suspense fallback={<LoadingCircularProgress open />}>
                        <PerformanceEnforcementAnalysisPage />
                      </Suspense>
                    ),
                  },
                ],
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PerformanceEnforcementAnalysisPage />
                  </Suspense>
                ),
              },
              {
                path: 'seller',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PerformanceSellerAnalysisPage />
                  </Suspense>
                ),
              },
              {
                path: 'compliance',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PerformanceMarketplaceComplianceRatePage />
                  </Suspense>
                ),
              },
              // piracy
              {
                path: PerformanceRouteNameEnum.SearchEngine,
                loader: async ({ params }) => {
                  if (!params.tab) {
                    return redirect('detected');
                  } else {
                    return null;
                  }
                },
                children: [
                  {
                    path: ':tab',
                    element: (
                      <Suspense fallback={<LoadingCircularProgress open />}>
                        <PiracyPerformanceEnforcementAnalysisPage
                          channelGroup={PerformanceRouteNameEnum.SearchEngine}
                        />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: PerformanceRouteNameEnum.SocialMediaVideo,
                loader: async ({ params }) => {
                  if (!params.tab) {
                    return redirect('detected');
                  } else {
                    return null;
                  }
                },
                children: [
                  {
                    path: ':tab',
                    element: (
                      <Suspense fallback={<LoadingCircularProgress open />}>
                        <PiracyPerformanceEnforcementAnalysisPage
                          channelGroup={PerformanceRouteNameEnum.SocialMediaVideo}
                        />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: PerformanceRouteNameEnum.ComplianceRate,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PiracyPerformanceComplianceRatePage />
                  </Suspense>
                ),
              },
              {
                path: PerformanceRouteNameEnum.DomainProfileAnalysis,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PiracyPerformanceDomainProfileAnalysisPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'customer-reports',
            loader: async () => {
              const hasAccess = await PermissionMap.PerformanceReport();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PerformanceReportsPage />
              </Suspense>
            ),
          },

          //#region >>>>> Portfolio
          {
            path: 'ip-portfolio/:id?',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <IpPortfolioPage />
              </Suspense>
            ),
          },
          {
            path: 'infringing-images',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <InfringingImagesPage />
              </Suspense>
            ),
          },
          {
            path: 'assets',
            children: [
              {
                path: '',
                index: true,
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <CounterfeitAssetsPage />
                  </Suspense>
                ),
              },
              {
                path: 'counterfeit',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <CounterfeitAssetsPage />
                  </Suspense>
                ),
              },
              {
                path: 'piracy',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <PiracyAssetsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'official-image/:brand_id?/:collection_id?',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <OfficialImagesPages />
              </Suspense>
            ),
          },
          {
            path: 'authorized-sellers',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <AuthorizedSeller />
              </Suspense>
            ),
          },
          //#endregion

          {
            path: 'pdf-viewer/:file',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PDFViewer />
              </Suspense>
            ),
          },
          {
            path: 'marketplace',
            loader: async () => {
              const hasAccess = await PermissionMap.Marketplace();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            children: [
              {
                index: true,
                element: <Navigate to={MarketplaceRouteEnum.Detection} replace />,
              },
              {
                path: ':tab/:listingId?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <MarketplacePage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'social-media',
            loader: async () => {
              const hasAccess = await PermissionMap.SocialMedia();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            children: [
              {
                index: true,
                loader: async () => {
                  const authData = await getUserInfoQuery();
                  if (authData?.use_piracy) {
                    return redirect('/social-media/detected');
                  } else {
                    return redirect('/social-media/detection');
                  }
                },
              },
              {
                path: ':tab/:listingId?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <SocialMediaPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'website-content',
            loader: async () => {
              const hasAccess = await PermissionMap.WebsiteContent();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            children: [
              {
                index: true,
                element: <Navigate to="detected" replace />,
              },
              {
                path: ':tab/:listingId?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <WebsiteContentPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'domain-names',
            loader: async () => {
              const hasAccess = await PermissionMap.DomainNames();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/ip-portfolio');
              }
            },
            children: [
              {
                index: true,
                element: <Navigate to="detected" replace />,
              },
              {
                path: ':tab/:listingId?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <DomainNamesPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <AxiosInterceptorWrapper />
              </Suspense>
            ),
            children: [
              {
                path: 'seller',
                loader: async () => {
                  const hasAccess = await PermissionMap.Seller();
                  if (hasAccess) {
                    return null; // bypass
                  } else {
                    return redirect('/ip-portfolio');
                  }
                },
                children: [
                  {
                    path: 'lab/:clusterId?',
                    element: (
                      <Suspense fallback={<LoadingCircularProgress open />}>
                        <SellerIntelligenceLabPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':detailPageType?/:id?/:subPageType?',
                    element: (
                      <Suspense fallback={<LoadingCircularProgress open />}>
                        <SellerPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'price-monitoring',
            loader: async () => {
              const hasAccess = await PermissionMap.PriceMonitoring();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/home');
              }
            },
            children: [
              {
                path: ':model_id?/:country?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <MAPMonitoring />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'conversational-assistant',
            loader: async () => {
              const hasAccess = await PermissionMap.ConversationalAssistant();
              if (hasAccess) {
                return null; // bypass
              } else {
                return redirect('/home');
              }
            },
            children: [
              {
                index: true,
                element: <Navigate to={MarqBrandEnumsCvassistantCvAssistantTabEnum.Detected} replace />,
              },
              {
                path: ':tab/:listingId?',
                element: (
                  <Suspense fallback={<LoadingCircularProgress open />}>
                    <ConversationalAssistantPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
];

export const marqCommerceClientRouter = createBrowserRouter(MarqCommerceRoutes);
